import React from 'react';
import {FieldMetaProps} from 'formik';
import {Pot} from '../../../schema-types/Pot';
import {mainPlanTypeType} from '../CreateRecommend/recommendFields';
import {transferFieldsType} from './transferFields';
import {getSummary} from '../CreateRecommend/RecommendSourceDestination/naming';
import {hasWrapper} from '../CreateRecommend/RecommendSourceDestination/utils';
import {TransferSourceForm} from './TransferSourceForm';
import {TransferExistingSubplans} from './TransferExistingSubplans';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import RecommendPlanForm from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {ShellPlan} from '../RecommendsSetUpNew/ShellPlan';
import {TransferShellPlanData} from './TransferHelpers';

interface TransferSourcePlanFormProps {
  pot: Pot;
  index: number;
  sourcePots: Pot[];
  mainPlanType: mainPlanTypeType;
  transferSourcesFieldMeta: FieldMetaProps<
    transferFieldsType['transferSources'] | undefined
  >;
  transferTo?: string;
  reviewId: string;
  isSelected: boolean;
  handleChange: (id: string) => void;
}

export const TransferSourcePlanForm: React.FC<TransferSourcePlanFormProps> = ({
  pot,
  index,
  sourcePots,
  mainPlanType,
  transferSourcesFieldMeta,
  transferTo,
  reviewId,
  isSelected,
  handleChange,
}) => {
  const validation = Object.keys(transferSourcesFieldMeta.value || {}).includes(pot.id)
    ? {
        totalFields: Object.keys(transferSourcesFieldMeta.value?.[pot.id] || {}).length,
        invalidFields: Object.keys(transferSourcesFieldMeta.error?.[pot.id] || {}).length,
      }
    : undefined;

  const subPlans = hasWrapper(mainPlanType) ? (
    <TransferExistingSubplans
      parentId={pot.id}
      pots={sourcePots}
      showEditing
      mainPlanType={mainPlanType}
    />
  ) : null;

  return (
    <RecommendPlanForm
      mode="multiple"
      id={pot.id}
      reccId={pot.originalOvrId ?? null}
      summary={getSummary(pot)}
      greyed={pot.id === transferTo}
      details={
        <TransferSourceForm
          pot={pot}
          id={pot.id}
          index={index}
          maxAmount={getCurrencyFloat(pot.totalValue)}
        />
      }
      curoId={pot.curoId}
      subItems={subPlans}
      validation={validation}
      sameReview={pot.review?.id === reviewId}
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};

interface TransferShellPlanFormProps {
  shellPlan: TransferShellPlanData;
  index: number;
  isSelected: boolean;
  handleChange: (id: string) => void;
}

export const TransferShellPlanForm: React.FC<TransferShellPlanFormProps> = ({
  shellPlan,
  index,
  isSelected,
  handleChange,
}) => {
  return (
    <RecommendPlanForm
      mode="multiple"
      id={shellPlan.id}
      reccId={null}
      summary={`New Source Plan #${index + 1} ${
        shellPlan.accountNumber ? `(${shellPlan.accountNumber})` : ''
      }`}
      details={
        <ShellPlan
          key={shellPlan.id}
          id={shellPlan.id}
          formikShellPlanKey={`transfer.transferSources.${shellPlan.id}`}
        />
      }
      sameReview
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};
